/* eslint-disable jsx-a11y/no-static-element-interactions */

/* eslint-disable jsx-a11y/click-events-have-key-events */
import { FC } from 'react'

import { Box, Center, IconButton } from '@chakra-ui/react'
import { AlertFillIcon, CommentDisplayIcon, ConfirmIcon, CuboidIcon, InputEditorCancelIcon } from '@editor/assets/icons'
import { EDITOR_CANVAS_CONTAINER_ID } from '@editor/config/constants'
import { PointArray, Position } from '@editor/interfaces/interfaces'
import { StylesProvider, Tooltip } from '@infraloop/common'
import { Html } from '@react-three/drei'
import { Vector3 } from 'three'

const ICON_BUTTON_PROPS: Omit<React.ComponentProps<typeof IconButton>, 'aria-label'> = {
  size: 'xs',
  variant: 'outline',
  fontSize: 'sm',
  borderWidth: '1px',
  borderRadius: '50%',
  borderColor: 'gray2.500',
  padding: '4px',
  backgroundColor: 'white',
  boxShadow: '0 0 3px white',
}

const CommentIcon: FC<{
  cartesianPosition?: Position
  cuboidPosition?: PointArray
  disabled: boolean
  visible: boolean
  isMoving: boolean
  onClick?: () => void
  onMouseUp?: (e: React.MouseEvent) => void
  onCancelMove?: () => void
  onConfirmMove?: () => void
}> = ({
  cartesianPosition,
  cuboidPosition,
  disabled,
  visible,
  isMoving,
  onClick,
  onMouseUp,
  onCancelMove,
  onConfirmMove,
}) => {
  const content = isMoving ? (
    <Center position="relative" className="comment-icon moving">
      <CommentDisplayIcon data-testid="commentDisplayIcon" />
      <IconButton
        {...ICON_BUTTON_PROPS}
        borderColor="{colors.red.500}"
        color="{colors.red.500}"
        aria-label="Cancel Move"
        // please keep this attribute for moving comment position feature
        aria-controls={EDITOR_CANVAS_CONTAINER_ID}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation()
          if (!disabled && onCancelMove) onCancelMove()
        }}
        onMouseUp={(e) => e.stopPropagation()}
        position="absolute"
        bottom={-30}
        left={-16}
      >
        <InputEditorCancelIcon pointerEvents="none" />
      </IconButton>
      <IconButton
        {...ICON_BUTTON_PROPS}
        borderColor="{colors.primary.500}"
        color="{colors.primary.500}"
        aria-label="Confirm Move"
        // please keep this attribute for moving comment position feature
        aria-controls={EDITOR_CANVAS_CONTAINER_ID}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation()
          if (!disabled && onConfirmMove) onConfirmMove()
        }}
        onMouseUp={(e) => e.stopPropagation()}
        position="absolute"
        bottom={-30}
        left={16}
      >
        <ConfirmIcon pointerEvents="none" />
      </IconButton>
    </Center>
  ) : (
    <Tooltip
      showArrow
      positioning={{ placement: 'bottom' }}
      label={!cartesianPosition?.x && !cuboidPosition && !isMoving ? '位置が指定されていません。' : ''}
      /* TODO: style migration needed - p=8px */
      /* TODO: style migration needed - fontWeight=normal */
      /* className="canvas-tooltip" */
    >
      <div
        onClick={() => {
          if (!disabled && onClick) onClick()
        }}
        onMouseUp={(e) => {
          if (!disabled && onMouseUp) onMouseUp(e)
        }}
      >
        <CommentDisplayIcon data-testid="commentDisplayIcon" />
        {cuboidPosition && (
          <Box position="absolute" top={5} left={7}>
            <CuboidIcon width={11} height={11} />
          </Box>
        )}
        {!cartesianPosition?.x && !cuboidPosition && (
          <Center position="absolute" color="white" zIndex={1} top={3} left={5}>
            <AlertFillIcon data-testid="unplacedCommentDisplayIcon" />
          </Center>
        )}
      </div>
    </Tooltip>
  )

  // Unplaced comment is grouped into a single drei-Html element outside of this component
  // Here we'll just return a standard HTML element
  if (!cartesianPosition?.x) {
    return (
      <Box
        cursor={disabled ? 'inherit' : 'pointer'}
        opacity={disabled ? '0.6' : '1'}
        visibility={!visible ? 'hidden' : 'visible'}
        padding="4px"
      >
        <Box width="24px" height="24px" position="relative">
          {content}
        </Box>
      </Box>
    )
  }

  return (
    <Html
      position={
        new Vector3(
          cartesianPosition?.x || cuboidPosition?.[0] || 0,
          cartesianPosition?.y || cuboidPosition?.[1] || 0,
          cartesianPosition?.z || cuboidPosition?.[2] || 0,
        )
      }
      style={{
        transform: 'translateY(-100%)',
        width: '24px',
        height: '24px',
        cursor: disabled ? 'inherit' : 'pointer',
        opacity: disabled ? '0.6' : '1',
        visibility: visible ? 'visible' : 'hidden',
        position: 'relative',
      }}
      zIndexRange={isMoving ? [1, 1] : [0, 0]}
    >
      <StylesProvider>{content}</StylesProvider>
    </Html>
  )
}

export default CommentIcon
